import React from 'react'
import PropTypes from 'prop-types'
import { prop, propEq, both, equals, includes } from 'ramda'
import strings from 'utils/strings'
import {
  SMARTPLEX_INPUT,
  SMARTPLEX_OUTPUT,
  SMART_SWITCH_GROUP,
  BASE_TYPE,
  LOGIC_SET,
  NATIVE_INPUTS,
  NATIVE_OUTPUTS,
  INTERFACE_CONNECTOR,
  FUNCTION,
  DESC_SCHEMA,
  ROWID_SCHEMA,
  PGN_SCHEMA,
  SWITCHID_SCHEMA,
  INPUT_TYPE_SCHEMA,
  OUPUT_PIN_SCHEMA,
  INPUT_PIN_SCHEMA,
  OUPUT_TYPE_SCHEMA,
  INTERFACE_PIN_TYPE_SCHEMA,
  INTERFACE_PIN_NUM_DESC_SCHEMA,
  SWITCHES,
  USER,
} from 'utils/constants'
import { MODULE_TYPE } from 'utils/constants/vehicle-constants'
import { chipTitleByFilterType } from 'i18n/displayConstants/displayFilterName'
import { Icon, Paragraph } from 'components/patterns'
import withStyles from './Chip.style'

const Chip = ({
  className,
  remove,
  pin,
  description,
  number,
  ssid,
  type,
  schema,
  nativeInputs,
  nativeOutputs,
  switches,
  value,
  filterType,
  filter1,
  filter2,
  filter3,
  user,
}) => {
  const hasSmartplexInput = includes(SMARTPLEX_INPUT)(filterType)
  const hasSmartplexOutput = includes(SMARTPLEX_OUTPUT)(filterType)
  const hasSmartSwitchType = includes(SMART_SWITCH_GROUP, filterType)
  const hasBaseType = equals(BASE_TYPE, filterType)
  const hasLogicSetType = equals(LOGIC_SET, filterType)
  const hasModuleType = equals(MODULE_TYPE, filterType)
  const hasNativeInputsType = equals(NATIVE_INPUTS, filterType)
  const hasNativeOutputsType = equals(NATIVE_OUTPUTS, filterType)
  const hasSwitchSignalsType = equals(SWITCHES, filterType)
  const hasInterfaceConnectorType = equals(INTERFACE_CONNECTOR, filterType)
  const hasFunctionType = equals(FUNCTION, filterType)

  const hasRowId = propEq('schema', ROWID_SCHEMA)(filter3)
  const hasPgn = propEq('schema', PGN_SCHEMA, filter3)
  const hasDesc = propEq('schema', DESC_SCHEMA, filter3)
  const hasSwitchId = propEq('schema', SWITCHID_SCHEMA, filter3)
  const hasRowIdAndDesc = both(
    prop('description'),
    propEq('schema', ROWID_SCHEMA)
  )(filter3)
  const hasInputType = propEq('schema', INPUT_TYPE_SCHEMA, filter2)
  const hasInputPin = propEq('schema', INPUT_PIN_SCHEMA, filter2)
  const hasOutputType = propEq('schema', OUPUT_TYPE_SCHEMA, filter2)
  const hasOutputPin = propEq('schema', OUPUT_PIN_SCHEMA, filter2)
  const hasInterfacePinType = equals(INTERFACE_PIN_TYPE_SCHEMA, schema)
  const hasInterfaceNumDescType = equals(INTERFACE_PIN_NUM_DESC_SCHEMA, schema)
  const hasUser = equals(USER, filterType)

  const smartplexInput = {
    title: chipTitleByFilterType[SMARTPLEX_INPUT],
    pin: `${strings.pin}: ${pin}`,
    description: `${strings.description}: ${description}`,
  }

  const smartplexOutput = {
    title: chipTitleByFilterType[SMARTPLEX_OUTPUT],
    pin: `${strings.pin}: ${pin}`,
    description: `${strings.description}: ${description}`,
  }

  const smartSwitch = {
    title: chipTitleByFilterType[SMART_SWITCH_GROUP],
    number: `${strings.switch_part_no}: ${number}`,
    description: `${strings.description}: ${description}`,
    ssid: `${strings.ssid}: ${ssid}`,
  }

  const baseTypeWithRowIdAndDesc = {
    title: chipTitleByFilterType[filterType],
    filter1Val: `${strings.vehicle_type}: ${prop('value', filter1)}`,
    filter2Val: `${prop('value', filter2)}`,
    rowId: `${strings.rowid}: ${prop('rowId', filter3)}`,
    description: `${strings.description}: ${prop('description', filter3)}`,
  }

  const baseTypeWithRowId = {
    title: chipTitleByFilterType[filterType],
    filter1Val: `${strings.vehicle_type}: ${prop('value', filter1)}`,
    filter2Val: `${prop('value', filter2)}`,
    rowId: `${strings.rowid}: ${prop('rowId', filter3)}`,
  }

  const baseTypeWithSwitchId = {
    title: chipTitleByFilterType[filterType],
    filter1Val: `${strings.vehicle_type}: ${prop('value', filter1)}`,
    filter2Val: `${prop('value', filter2)}`,
    switchId: `${strings.switch_id}: ${prop('label', filter3)}`,
  }

  const baseTypeWithPgn = {
    title: chipTitleByFilterType[filterType],
    filter1Val: `${strings.vehicle_type}: ${prop('value', filter1)}`,
    filter2Val: `${prop('value', filter2)}`,
    pgn: `${strings.pgn}: ${prop('pgn', filter3)}`,
  }

  const baseTypeWithDesc = {
    title: chipTitleByFilterType[filterType],
    filter1Val: `${strings.vehicle_type}: ${prop('value', filter1)}`,
    filter2Val: `${prop('value', filter2)}`,
    description: `${strings.description}: ${prop('label', filter3)}`,
  }

  const logicSetType = {
    title: chipTitleByFilterType[filterType],
    description: `${strings.description}: ${description}`,
  }

  const moduleTypeInputOrOutput = {
    title: chipTitleByFilterType[filterType],
    moduleType: `${strings.ecu}: ${prop('moduleType', filter1)}`,
    inputType: `${strings.input_output_type(
      prop('thirdValue', filter2)
    )}: ${prop('type', filter2)}`,
  }

  const moduleTypeByPinAndDesc = {
    title: chipTitleByFilterType[filterType],
    moduleType: `${strings.ecu}: ${prop('moduleType', filter1)}`,
    pin: `${strings.pin}: ${prop('pin', filter2)}`,
    description: `${strings.description}: ${prop('description', filter2)}`,
  }

  const nativeInputsType = {
    title: `${chipTitleByFilterType[filterType]}:`,
    nativeSignal: nativeInputs,
  }

  const nativeOutputsType = {
    title: `${chipTitleByFilterType[filterType]}:`,
    nativeSignal: nativeOutputs,
  }

  const switchSignalType = {
    title: `${chipTitleByFilterType[filterType]}:`,
    nativeSignal: switches,
  }

  const interfaceConnectorByPinDesc = {
    title: chipTitleByFilterType[filterType],
    pin: `${strings.pin_number}: ${pin}`,
    description: `${strings.description}: ${description}`,
  }

  const interfaceConnectorByType = {
    title: chipTitleByFilterType[filterType],
    pin: `${strings.type_cap}: ${type}`,
  }

  const functionType = {
    title: chipTitleByFilterType[filterType],
    description: `${strings.description}: ${description}`,
  }

  const userType = {
    title: `${chipTitleByFilterType[filterType]}:`,
    description: user,
  }

  const chipDataDictionary = () => {
    if (hasSmartplexInput) return smartplexInput
    if (hasSmartplexOutput) return smartplexOutput
    if (hasSmartSwitchType) return smartSwitch
    if (hasBaseType) {
      if (hasRowIdAndDesc) return baseTypeWithRowIdAndDesc
      if (hasRowId) return baseTypeWithRowId
      if (hasPgn) return baseTypeWithPgn
      if (hasDesc) return baseTypeWithDesc
      if (hasSwitchId) return baseTypeWithSwitchId
    }
    if (hasLogicSetType) return logicSetType
    if (hasModuleType) {
      if (hasInputPin || hasOutputPin) return moduleTypeByPinAndDesc
      if (hasInputType || hasOutputType) return moduleTypeInputOrOutput
    }
    if (hasNativeInputsType) return nativeInputsType
    if (hasNativeOutputsType) return nativeOutputsType
    if (hasSwitchSignalsType) return switchSignalType
    if (hasInterfaceConnectorType) {
      if (hasInterfaceNumDescType) return interfaceConnectorByPinDesc
      if (hasInterfacePinType) return interfaceConnectorByType
    }
    if (hasFunctionType) return functionType
    if (hasUser) return userType
    return null
  }

  const renderChip = Object.values(chipDataDictionary()).map((chipValue) => (
    <Paragraph key={chipValue}>{chipValue}</Paragraph>
  ))

  return (
    <div className={className}>
      <>
        <Icon icon="close" onClick={() => remove(value)} />
        {renderChip}
      </>
    </div>
  )
}

export default withStyles(Chip)

Chip.defaultProps = {
  filter1: {},
  filter2: {},
  filter3: {},
}

Chip.propTypes = {
  type: PropTypes.string,
  filterType: PropTypes.string,
  className: PropTypes.string,
  value: PropTypes.string,
  pin: PropTypes.string,
  description: PropTypes.string,
  schema: PropTypes.string,
  number: PropTypes.string,
  logicSet: PropTypes.string,
  nativeOutputs: PropTypes.string,
  nativeInputs: PropTypes.string,
  switches: PropTypes.string,
  moduleType: PropTypes.string,
  ssid: PropTypes.string,
  user: PropTypes.string,
  remove: PropTypes.func,
  filter1: PropTypes.object,
  filter2: PropTypes.object,
  filter3: PropTypes.object,
}
